/**
 * SessionTimeoutWarning - Warns user before session expires
 * Auto-logout is DISABLED by default - can be enabled in settings
 * Shows warning 5 minutes before token expiry (only if enabled)
 */

import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  LinearProgress
} from '@mui/material';
import WarningIcon from '@mui/icons-material/Warning';
import TokenManager from '../../core/auth/TokenManager';
import PartnerAuthService from '../../core/auth/PartnerAuthService';

interface SessionTimeoutWarningProps {
  onLogout: () => void;
  warningThreshold?: number; // seconds before expiry to show warning (default: 300 = 5 min)
  enableAutoLogout?: boolean; // Enable auto-logout on session expiry (default: false)
}

export const SessionTimeoutWarning: React.FC<SessionTimeoutWarningProps> = ({
  onLogout,
  warningThreshold = 300,
  enableAutoLogout = false // DISABLED BY DEFAULT
}) => {
  const [showWarning, setShowWarning] = useState(false);
  const [timeRemaining, setTimeRemaining] = useState(0);

  useEffect(() => {
    // If auto-logout is disabled, don't show warning or track expiry
    if (!enableAutoLogout) {
      return;
    }

    const checkSessionTimeout = () => {
      const timeUntilExpiry = TokenManager.getTimeUntilExpiry();
      
      // Show warning if time remaining is less than threshold
      if (timeUntilExpiry > 0 && timeUntilExpiry <= warningThreshold) {
        setShowWarning(true);
        setTimeRemaining(timeUntilExpiry);
      } else if (timeUntilExpiry <= 0) {
        // Session expired - only logout if auto-logout is enabled
        setShowWarning(false);
        onLogout();
      } else {
        setShowWarning(false);
      }
    };

    // Check immediately
    checkSessionTimeout();

    // Check every 10 seconds
    const interval = setInterval(checkSessionTimeout, 10000);
    
    // Update countdown every second when warning is shown
    let countdownInterval: NodeJS.Timeout;
    if (showWarning) {
      countdownInterval = setInterval(() => {
        const timeUntilExpiry = TokenManager.getTimeUntilExpiry();
        setTimeRemaining(timeUntilExpiry);
        
        if (timeUntilExpiry <= 0) {
          clearInterval(countdownInterval);
          onLogout();
        }
      }, 1000);
    }

    return () => {
      clearInterval(interval);
      if (countdownInterval) clearInterval(countdownInterval);
    };
  }, [showWarning, warningThreshold, onLogout, enableAutoLogout]);

  const handleExtendSession = async () => {
    try {
      await PartnerAuthService.refreshAccessToken();
      setShowWarning(false);
    } catch (error) {
      console.error('Failed to extend session:', error);
      onLogout();
    }
  };

  const formatTime = (seconds: number): string => {
    const minutes = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${minutes}:${secs.toString().padStart(2, '0')}`;
  };

  return (
    <Dialog
      open={showWarning}
      onClose={() => {}} // Prevent closing by clicking outside
      disableEscapeKeyDown
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <WarningIcon sx={{ color: '#f59e0b' }} />
        Session Timeout Warning
      </DialogTitle>
      
      <DialogContent>
        <Box sx={{ py: 2 }}>
          <Typography variant="body1" sx={{ mb: 2 }}>
            Your session will expire in <strong>{formatTime(timeRemaining)}</strong>
          </Typography>
          
          <Typography variant="body2" color="textSecondary" sx={{ mb: 3 }}>
            Click "Extend Session" to continue working, or you will be automatically logged out.
          </Typography>

          <LinearProgress
            variant="determinate"
            value={(timeRemaining / warningThreshold) * 100}
            sx={{ mb: 2 }}
          />
        </Box>
      </DialogContent>

      <DialogActions sx={{ p: 2 }}>
        <Button
          onClick={onLogout}
          color="error"
          variant="outlined"
        >
          Logout Now
        </Button>
        <Button
          onClick={handleExtendSession}
          color="primary"
          variant="contained"
          autoFocus
        >
          Extend Session
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SessionTimeoutWarning;
